/**
 * "Report a problem": a short paper form over the game (docs/UI.md "Report a problem"). What happened (required),
 * a category (optional), SEND, SAVE AS FILE, CANCEL. Says plainly what is sent with the note. Keys typed into it
 * never reach the game; Escape cancels. Touch: big targets, the on-screen keyboard is the browser's.
 * DOM only; the system (feedbackSystem.ts) decides what to do with the note.
 */
import { REPORT_CATEGORIES, REPORT_LIMITS, type ReportCategory } from '../../shared/feedback';
import './feedback.css';

export interface ReportFormResult {
  message: string;
  category: ReportCategory | null;
}

export interface ReportFormOptions {
  /** One line about what goes with the note (device, fps, position, last log lines...). */
  attachments: string;
  online: boolean;
  onSend(r: ReportFormResult): Promise<'sent' | 'queued' | 'dropped'>;
  onSave(r: ReportFormResult): void;
  onClose(): void;
}

const CATEGORY_LABEL: Record<ReportCategory, string> = {
  bug: 'Something broke',
  stuck: 'Stuck / can’t move',
  cars: 'Cars / getting in',
  online: 'Online / connection',
  money: 'Money / items',
  performance: 'Slow / stutters',
  controls: 'Controls / touch',
  other: 'Something else',
};

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text: string | null, parent?: HTMLElement): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== null) n.textContent = text;
  parent?.appendChild(n);
  return n;
}

export class ReportForm {
  readonly root: HTMLElement;
  private readonly text: HTMLTextAreaElement;
  private readonly category: HTMLSelectElement;
  private readonly status: HTMLElement;
  private readonly send: HTMLButtonElement;
  private busy = false;

  constructor(parent: HTMLElement, private readonly o: ReportFormOptions) {
    this.root = el('div', 'bh-ui bh-report', null, parent); // bh-ui: the UI's tokens (paper, ink, fonts, --u)
    // Menu navigation (W/S/Space/Enter on the pause menu under us) leaves keys typed here alone (src/ui/uiSystem.ts).
    this.root.dataset.bhTextEntry = '';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-labelledby', 'bh-report-title');
    const card = el('div', 'card bh-glass blur', null, this.root);
    const head = el('div', 'head', null, card);
    const title = el('h2', '', 'Report a problem', head);
    title.id = 'bh-report-title';
    const x = el('button', 'x bh-x', '✕', head);
    x.type = 'button';
    x.setAttribute('aria-label', 'Close');
    x.addEventListener('click', () => this.o.onClose());

    const label = el('label', 'field', null, card);
    el('span', 'lab', 'What happened?', label);
    this.text = el('textarea', '', null, label);
    this.text.rows = 4;
    this.text.maxLength = REPORT_LIMITS.message;
    this.text.placeholder = 'A line or two is plenty: what you did, what you expected, what happened instead.';
    this.text.addEventListener('input', () => this.refresh());

    const cat = el('label', 'field', null, card);
    el('span', 'lab', 'What’s it about? (optional)', cat);
    this.category = el('select', '', null, cat);
    el('option', '', 'Choose…', this.category).value = '';
    for (const c of REPORT_CATEGORIES) el('option', '', CATEGORY_LABEL[c], this.category).value = c;

    el('p', 'note', `Sent with your note: ${o.attachments}`, card);
    if (!o.online) el('p', 'note', 'Playing offline: it goes to the game server now if it can be reached, otherwise it waits on this device and goes the next time it can.', card);
    this.status = el('p', 'status', '', card);
    this.status.setAttribute('role', 'status');

    const actions = el('div', 'actions', null, card);
    this.send = el('button', 'go', 'Send', actions);
    this.send.type = 'button';
    this.send.addEventListener('click', () => void this.submit());
    const save = el('button', 'back', 'Save as file', actions);
    save.type = 'button';
    save.addEventListener('click', () => this.o.onSave(this.value()));
    const cancel = el('button', 'back', 'Cancel', actions);
    cancel.type = 'button';
    cancel.addEventListener('click', () => this.o.onClose());

    // Typing here is not driving: keys stay in the form (the game listens on window). Escape cancels.
    this.root.addEventListener('keydown', (e) => {
      if (e.code === 'F8') return; // the feedback system's toggle closes it
      e.stopPropagation();
      if (e.key === 'Escape') {
        e.preventDefault();
        this.o.onClose();
      }
    });
    this.root.addEventListener('keyup', (e) => e.stopPropagation());
    this.refresh();
    setTimeout(() => this.text.focus(), 0);
  }

  value(): ReportFormResult {
    const c = this.category.value;
    return { message: this.text.value.trim(), category: (REPORT_CATEGORIES as readonly string[]).includes(c) ? (c as ReportCategory) : null };
  }

  private refresh(): void {
    this.send.disabled = this.busy || this.text.value.trim().length === 0;
  }

  private async submit(): Promise<void> {
    const v = this.value();
    if (!v.message || this.busy) return;
    this.busy = true;
    this.refresh();
    this.status.textContent = 'Sending…';
    const r = await this.o.onSend(v).catch(() => 'queued' as const);
    this.busy = false;
    if (r === 'sent') {
      this.status.textContent = 'Thanks — it’s with the host now.';
      this.text.value = '';
      this.category.value = '';
      setTimeout(() => this.o.onClose(), 1400);
    } else if (r === 'queued') {
      this.status.textContent = 'Couldn’t reach the server — saved on this device; it goes the next time the server can be reached. “Save as file” keeps a copy.';
    } else {
      this.status.textContent = 'The server refused that report. “Save as file” keeps a copy you can send another way.';
    }
    this.refresh();
    // A disabled button drops focus to the page: put it back in the form (keys, Escape).
    if (this.root.isConnected) this.text.focus();
  }

  dispose(): void {
    this.root.remove();
  }
}
